import React, { useRef, useState } from 'react';
import './ResizableDualListBox.css';

export default function ResizableDualListBox({
  leftTitle = 'Available',
  leftCount,
  leftItems = [],
  leftSelectedIds = [],
  onLeftSelectionChange,
  renderLeftOption,
  getLeftItemId = (item) => item.student_id ?? item.teacher_id ?? item.id,
  leftChildren,

  rightTitle = 'Assigned',
  rightCount,
  rightItems = [],
  rightSelectedIds = [],
  onRightSelectionChange,
  renderRightOption,
  getRightItemId = (item) => item.student_id ?? item.teacher_id ?? item.id,
  rightChildren,

  onAdd,
  addDisabled = false,
  addLabel = '→ Add',
  addButtonStyle,

  onRemove,
  removeDisabled = false,
  removeLabel = 'Remove ←',
  removeButtonStyle,

  defaultHeight = 160,
  minHeight = 120,
  minWidth = 180,
  hintText = 'Drag corner ↘ to resize freely',
}: any) {
  const leftBoxRef = useRef<any>(null);
  const rightBoxRef = useRef<any>(null);
  const [activePreset, setActivePreset] = useState<any>(null);

  const handleSetPreset = (height) => {
    if (leftBoxRef.current) {
      leftBoxRef.current.style.height = `${height}px`;
      leftBoxRef.current.style.width = '';
    }
    if (rightBoxRef.current) {
      rightBoxRef.current.style.height = `${height}px`;
      rightBoxRef.current.style.width = '';
    }
    setActivePreset(height);
  };

  const handleReset = () => {
    if (leftBoxRef.current) {
      leftBoxRef.current.style.height = `${defaultHeight}px`;
      leftBoxRef.current.style.width = '';
    }
    if (rightBoxRef.current) {
      rightBoxRef.current.style.height = `${defaultHeight}px`;
      rightBoxRef.current.style.width = '';
    }
    setActivePreset(null);
  };

  const computedLeftCount = leftCount !== undefined ? leftCount : (leftChildren ? React.Children.count(leftChildren) : leftItems.length);
  const computedRightCount = rightCount !== undefined ? rightCount : (rightChildren ? React.Children.count(rightChildren) : rightItems.length);

  return (
    <div className="ae-dual-box-container">
      <div className="ae-dual-box-toolbar">
        <div className="ae-dual-box-presets" role="group" aria-label="Box height presets">
          <span className="ae-dual-box-preset-label">Box Size:</span>
          <button
            type="button"
            className={`ae-dual-box-preset-btn ${activePreset === 140 ? 'active' : ''}`}
            onClick={() => handleSetPreset(140)}
            title="Compact height (140px)"
          >
            Compact
          </button>
          <button
            type="button"
            className={`ae-dual-box-preset-btn ${activePreset === 250 ? 'active' : ''}`}
            onClick={() => handleSetPreset(250)}
            title="Medium height (250px)"
          >
            Medium
          </button>
          <button
            type="button"
            className={`ae-dual-box-preset-btn ${activePreset === 400 ? 'active' : ''}`}
            onClick={() => handleSetPreset(400)}
            title="Large height (400px)"
          >
            Large
          </button>
          <button
            type="button"
            className="ae-dual-box-preset-btn"
            onClick={handleReset}
            title="Reset to default size"
          >
            Reset
          </button>
        </div>
        <span className="ae-dual-box-hint" title="You can drag the bottom-right corner of each box to customize width and height">
          💡 {hintText}
        </span>
      </div>

      <div className="ae-dual-box-row">
        {/* Left Column */}
        <div className="ae-dual-box-col">
          <div className="ae-dual-box-header">
            <span className="ae-dual-box-title">{leftTitle}</span>
            <span className="ae-dual-box-count">({computedLeftCount} {computedLeftCount === 1 ? 'item' : 'items'})</span>
          </div>
          <div
            ref={leftBoxRef}
            className="ae-resizable-user-box"
            style={{ height: defaultHeight, minHeight, minWidth }}
            title="Drag bottom-right corner to resize (width & height)"
          >
            <select
              multiple
              className="ae-resizable-select"
              aria-label={leftTitle}
              value={leftSelectedIds.map(String)}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions).map((o) => Number(o.value));
                onLeftSelectionChange?.(selected);
              }}
            >
              {leftChildren || (leftItems.length === 0 ? (
                <option disabled value="">No items available</option>
              ) : (
                leftItems.map((item) => {
                  const id = getLeftItemId(item);
                  return (
                    <option key={id} value={id}>
                      {renderLeftOption ? renderLeftOption(item) : String(item)}
                    </option>
                  );
                })
              ))}
            </select>
            <span className="ae-resize-grip-indicator" aria-hidden="true">
              <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor">
                <path d="M14 14H12V12H14V14ZM14 10H12V8H14V10ZM10 14H8V12H10V14ZM14 6H12V4H14V6ZM10 10H8V8H10V10ZM6 14H4V12H6V14Z" />
              </svg>
            </span>
          </div>
        </div>

        {/* Action Buttons Column */}
        <div className="ae-dual-box-actions">
          <button
            type="button"
            className="ae-btn-primary"
            style={{ padding: '8px 16px', fontSize: 13, ...addButtonStyle }}
            disabled={addDisabled}
            onClick={onAdd}
          >
            {addLabel}
          </button>
          <button
            type="button"
            className="ae-btn-soft"
            style={{ padding: '8px 16px', fontSize: 13, color: 'var(--brand-red, #dc2626)', borderColor: 'var(--brand-red, #dc2626)', ...removeButtonStyle }}
            disabled={removeDisabled}
            onClick={onRemove}
          >
            {removeLabel}
          </button>
        </div>

        {/* Right Column */}
        <div className="ae-dual-box-col">
          <div className="ae-dual-box-header">
            <span className="ae-dual-box-title">{rightTitle}</span>
            <span className="ae-dual-box-count">({computedRightCount} {computedRightCount === 1 ? 'item' : 'items'})</span>
          </div>
          <div
            ref={rightBoxRef}
            className="ae-resizable-user-box"
            style={{ height: defaultHeight, minHeight, minWidth }}
            title="Drag bottom-right corner to resize (width & height)"
          >
            <select
              multiple
              className="ae-resizable-select"
              aria-label={rightTitle}
              value={rightSelectedIds.map(String)}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions).map((o) => Number(o.value));
                onRightSelectionChange?.(selected);
              }}
            >
              {rightChildren || (rightItems.length === 0 ? (
                <option disabled value="">No items available</option>
              ) : (
                rightItems.map((item) => {
                  const id = getRightItemId(item);
                  return (
                    <option key={id} value={id}>
                      {renderRightOption ? renderRightOption(item) : String(item)}
                    </option>
                  );
                })
              ))}
            </select>
            <span className="ae-resize-grip-indicator" aria-hidden="true">
              <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor">
                <path d="M14 14H12V12H14V14ZM14 10H12V8H14V10ZM10 14H8V12H10V14ZM14 6H12V4H14V6ZM10 10H8V8H10V10ZM6 14H4V12H6V14Z" />
              </svg>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
